(function () {
  'use strict';

  if (window.CommenseTrackingQueue) return;
  const recordPrefix = 'COMMENSE_TRACKING_RECORD:';
  const legacyStorageKey = 'AWS_DATA';
  // Keep the existing lock name to coordinate with pages running the previous queue code.
  const legacyLockKey = 'COMMENSE_TRACKING_LEGACY_MIGRATION';
  const retryInterval = 5000;
  const leaseDuration = 5 * 60 * 1000;
  const leaseRenewalInterval = leaseDuration / 3;
  const lockTimeout = 5000;
  const owner = createRecordId();
  const inFlight = new Set();
  const reportedLockFailures = new Set();
  let databasePromise, sender, started = false;

  function createRecordId() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + '-' + Math.random().toString(36).slice(2);
  }

  // Each record has its own key: concurrent tabs cannot overwrite another enqueue.
  // The id belongs to the browser queue; the event sent to Kinesis is unchanged.
  function enqueue(data) {
    const key = recordPrefix + createRecordId();
    localStorage.setItem(key, JSON.stringify(data));
    return key;
  }

  // Preserve the original readiness poll; SDK callbacks determine request completion.
  // startRequest returns false while not ready, otherwise starts one SDK request.
  function sendRequest(startRequest) {
    return new Promise(function (resolve, reject) {
      let settled = false;
      function complete(error) {
        if (settled) return;
        settled = true;
        clearInterval(readinessTimer);
        if (error) reject(error); else resolve();
      }
      function attempt() {
        if (settled) return;
        try {
          const request = startRequest(complete);
          if (request !== false) clearInterval(readinessTimer);
        } catch (error) { complete(error); }
      }
      const readinessTimer = setInterval(attempt, 100);
    });
  }

  function reportLockFailure(step, error) {
    if (reportedLockFailures.has(step)) return;
    reportedLockFailures.add(step);
    console.error('tracking_queue step=' + step + ' owner=' + owner, error);
  }

  function openDatabase() {
    if (!databasePromise) {
      databasePromise = new Promise(function (resolve, reject) {
        const request = indexedDB.open('commense-tracking-locks', 1);
        let settled = false;
        const timer = setTimeout(function () { fail(new Error('IndexedDB open timed out')); }, lockTimeout);
        function fail(error) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(error);
        }
        request.onupgradeneeded = function () {
          if (settled) { request.transaction.abort(); return; }
          request.result.createObjectStore('locks');
        };
        request.onsuccess = function () {
          if (settled) { request.result.close(); return; }
          settled = true;
          clearTimeout(timer);
          request.result.onversionchange = function () { request.result.close(); databasePromise = null; };
          resolve(request.result);
        };
        request.onerror = function () { fail(request.error); };
        request.onblocked = function () { fail(new Error('IndexedDB open blocked')); };
      }).catch(function (error) { databasePromise = null; throw error; });
    }
    return databasePromise;
  }

  // Older browsers without Web Locks use a short, atomic IndexedDB lease transaction.
  // No transaction stays open while a network request is pending.
  async function updateLease(key, action, updateStorage) {
    const database = await openDatabase();
    return new Promise(function (resolve, reject) {
      const transaction = database.transaction('locks', 'readwrite');
      const store = transaction.objectStore('locks');
      const request = store.get(key);
      let accepted = false;
      request.onsuccess = function () {
        const lease = request.result, now = Date.now();
        if (action === 'claim') {
          if (lease && lease.expiresAt > now) return;
        } else if (action !== 'acknowledge' &&
          (!lease || lease.owner !== owner || (action !== 'release' && lease.expiresAt <= now))) {
          if (action === 'commit') {
            console.error('tracking_queue step=lease_lost key=' + key + ' owner=' + owner +
              ' current_owner=' + (lease ? lease.owner : '') + ' expires_at=' + (lease ? lease.expiresAt : 0) + ' now=' + now);
          }
          return;
        }
        try {
          if ((action === 'commit' || action === 'acknowledge') && updateStorage() === false) return;
          accepted = true;
          // A successful send acknowledges only its immutable record, never another owner's lease.
          if (action === 'acknowledge') return;
          if (action === 'release') {
            store.delete(key);
          } else {
            store.put({ owner: owner, expiresAt: now + leaseDuration }, key);
          }
        } catch (error) {
          reject(error);
          transaction.abort();
        }
      };
      transaction.oncomplete = function () { resolve(accepted); };
      transaction.onerror = transaction.onabort = function () { reject(transaction.error); };
    });
  }

  async function withRecordLock(key, work) {
    function commit(updateStorage) { return updateStorage() !== false; }
    if (navigator.locks && navigator.locks.request) {
      let callbackStarted = false;
      try {
        return await navigator.locks.request(key, { ifAvailable: true }, function (lock) {
          callbackStarted = true;
          if (!lock) return;
          return work(commit, commit);
        });
      } catch (error) {
        // Never bypass a busy lock or retry a failure from the sender as unlocked work.
        if (callbackStarted) throw error;
        reportLockFailure('web_lock_unavailable', error);
      }
    }
    let claimed;
    try { claimed = await updateLease(key, 'claim'); } catch (error) {
      reportLockFailure('unlocked_fallback', error);
      return work(commit, commit);
    }
    if (!claimed) return;
    // Keep an active SDK request's lease valid without imposing a new request deadline.
    const renewalTimer = setInterval(function () {
      updateLease(key, 'renew').then(function (renewed) {
        if (renewed) return;
        clearInterval(renewalTimer);
        console.error('tracking_queue step=lease_renew_lost key=' + key + ' owner=' + owner);
      }).catch(function (error) {
        clearInterval(renewalTimer);
        console.error('tracking_queue step=lease_renew_failed key=' + key + ' owner=' + owner, error);
      });
    }, leaseRenewalInterval);
    try {
      return await work(
        function (updateStorage) { return updateLease(key, 'commit', updateStorage); },
        function (updateStorage) { return updateLease(key, 'acknowledge', updateStorage); }
      );
    } finally {
      clearInterval(renewalTimer);
      await updateLease(key, 'release');
    }
  }

  async function migrateLegacyRecords() {
    if (inFlight.has(legacyLockKey)) return;
    inFlight.add(legacyLockKey);
    try {
      if (!localStorage.getItem(legacyStorageKey)) return;
      await withRecordLock(legacyLockKey, function (commit) {
        return commit(function () {
          const raw = localStorage.getItem(legacyStorageKey);
          if (!raw) return;
          let records = JSON.parse(raw) || [];
          records = Array.isArray(records) ? records : [records];
          const importedKeys = [];
          try {
            records.forEach(function (data) { importedKeys.push(enqueue(data)); });
            localStorage.removeItem(legacyStorageKey);
          } catch (error) {
            importedKeys.forEach(function (key) { localStorage.removeItem(key); });
            throw error;
          }
        });
      });
    } catch (error) {
      // Migration is best effort: keep the old batch, but never block new records.
      console.error('tracking_queue step=legacy_migration_failed key=' + legacyStorageKey, error);
    } finally {
      inFlight.delete(legacyLockKey);
    }
  }

  // Immediate delivery and periodic recovery share the same record lock and acknowledgement.
  async function flushRecord(key) {
    if (!sender || inFlight.has(key)) return;
    inFlight.add(key);
    try {
      await withRecordLock(key, async function (commit, acknowledge) {
        const raw = localStorage.getItem(key);
        if (!raw) return;
        const data = JSON.parse(raw);
        await sender(data);
        await acknowledge(function () {
          if (localStorage.getItem(key) !== raw) return false;
          localStorage.removeItem(key);
        });
      });
    } catch (error) {
      // Failed records keep their original key and payload for the next retry.
      console.error('tracking_queue step=send_failed key=' + key + ' owner=' + owner, error);
    } finally {
      inFlight.delete(key);
    }
  }

  async function flushRecords() {
    if (!sender) return;
    try {
      const keys = [];
      for (let index = 0; index < localStorage.length; index++) {
        const key = localStorage.key(index);
        if (key && key.indexOf(recordPrefix) === 0) keys.push(key);
      }
      await Promise.all(keys.map(flushRecord));
    } catch (error) {
      console.error('tracking_queue step=flush_failed', error);
    }
  }

  async function flush() {
    if (!sender) return;
    // A pending migration lock must not delay consumption of existing new records.
    await Promise.all([flushRecords(), migrateLegacyRecords().then(flushRecords)]);
  }

  function start(sendRecord, enableAutomaticFlush = true) {
    sender = sendRecord;
    if (started) return;
    started = true;
    flush();
    if (enableAutomaticFlush) setInterval(flush, retryInterval);
  }

  window.CommenseTrackingQueue = { enqueue: enqueue, start: start, flush: flush, flushRecord: flushRecord, sendRequest: sendRequest };
})();
